<div id="application-editor-container" class="content-block flex-left-column light-control-host">
  <form id="scroll-id" [formGroup]="applicationFormModel" (ngSubmit)="saveApplication()">
    <div class="header-container">
      <div>
        <h1 id="application-settings-header">Application Settings</h1>
      </div>
      <div class="flex-center-row form-controls">
        <button
          id="cancel-button"
          data-cy="cancel-button"
          type="button"
          class="arpio-secondary-button"
          (click)="cancelClick()"
          *ngIf="!saving && permissionsLoaded"
        >
          <span *ngIf="canModify">CANCEL</span>
          <span *ngIf="!canModify"><i class="fa-utility fa-semibold fa-arrow-left"></i> GO BACK</span>
        </button>
        <span *ngIf="!saving && !permissionsLoaded">
          <i class="fa-solid fa-spinner fa-spin"></i>
        </span>
        <button
          data-cy="application-save-button"
          id="save-button"
          class="arpio-button margin-left"
          *ngIf="!saving && canModify; else savingBlock"
          type="submit"
          [disabled]="!applicationFormModel.valid || !canModify"
        >
          SAVE
        </button>
        <ng-template #savingBlock>
          <h2 *ngIf="canModify">
            <i class="fa-solid fa-spinner fa-spin"></i>
          </h2>
        </ng-template>
      </div>
    </div>
    <div *ngIf="permissionsLoaded && !canModify" class="notice-block">
      You have read-only access to this application. You cannot modify any settings or resources. Contact your
      administrator to request modification permissions.
    </div>
    <div *ngIf="permissionsLoaded && canModify && !canModify" class="notice-block">
      You do not have permission to edit application resources. You need Manager level access or higher.
    </div>

    <!-- Tabs Navigation -->
    <app-tabs
      id="application-editor-tabs"
      [tabs]="tabs"
      [useRouting]="false"
      [activeTabIndex]="activeTabIndex"
      (tabChange)="onTabChange($event)"
    ></app-tabs>

    <!-- GENERAL TAB CONTENT -->
    <div *ngIf="activeTab === 'general'" class="tab-content" id="general-tab-content" data-cy="general-tab-content">
      <div
        *ngIf="
          applicationFormModel.errors?.['existingAppWithSameName'] &&
          (applicationFormModel.touched || applicationFormModel.dirty)
        "
        class="error margin-top margin-bottom"
      >
        This application cannot be saved because another application exists with the same name.
      </div>

      <!-- Basic Settings Section -->
      <div class="settings-section">
        <h3 class="section-header">Basic Settings</h3>
        <div class="section-content">
          <label class="field-label">Your application name:</label>
          <input
            id="application-name-input"
            class="arpio-input full-width"
            type="text"
            formControlName="applicationName"
            placeholder="Application name"
            focus-element
            [disabled]="!canModify"
            [readOnly]="!canModify"
          />
        </div>
      </div>

      <!-- Recovery Point Objective Section -->
      <div class="settings-section">
        <div class="section-header-with-value">
          <h3 class="section-header">Recovery Point Objective</h3>
          <span class="header-value">{{ rpoText }}</span>
        </div>
        <div class="section-content">
          <p class="field-label">The maximum amount of data loss acceptable during a disaster, measured in time:</p>
          <rpo-range-slider
            [enableRealTimeRpos]="enableRealTimeRpo"
            formControlName="applicationRpo"
            [class.pointer-events-none]="!canModify"
            [class.opacity-50]="!canModify"
          ></rpo-range-slider>
          <about-real-time-replication
            *ngIf="isRealTimeRpo"
            [applicationType]="applicationType"
          ></about-real-time-replication>
        </div>
      </div>

      <!-- Notifications Section -->
      <div class="settings-section">
        <h3 class="section-header">Notifications</h3>
        <div class="section-content">
          <label class="field-label">Email recipients for alerts and notifications:</label>
          <string-list-control
            class="full-width"
            [class.pointer-events-none]="!canModify"
            [class.opacity-50]="!canModify"
            [validationFunction]="isValidEmail"
            formControlName="applicationNotificationEmails"
          ></string-list-control>
        </div>
      </div>

      <!-- Add Ons Section (conditional) -->
      <ng-container *ngLet="accountIntegrations as accountInts">
        <div *ngIf="accountInts.length > 0" class="settings-section">
          <h3 class="section-header">Add Ons</h3>
          <div class="section-content">
            <application-integrations-form-control
              formControlName="applicationIntegrationIds"
              [accountIntegrations]="accountInts"
              [disabled]="!canModify"
              [readOnly]="!canModify"
            ></application-integrations-form-control>
          </div>
        </div>
      </ng-container>

      <!-- Access Method Section (AWS with StackSets capability only) -->
      <div *ngIf="showAccessTab" id="access-method-section" class="settings-section">
        <div class="section-header-with-value">
          <h3 class="section-header">Access Method</h3>
          <span *ngIf="!accessMethodLoading" class="header-value">{{
            selectedAccessMethod === AccessMethod.STACKSETS ? 'AWS StackSets' : 'CloudFormation'
          }}</span>
          <span *ngIf="accessMethodLoading" class="header-value">
            <i class="fa-solid fa-spinner fa-spin"></i>
          </span>
        </div>
        <div class="section-content">
          <!-- Loading state -->
          <div *ngIf="accessMethodLoading" class="access-method-loading">
            <i class="fa-solid fa-spinner fa-spin"></i> Loading access configuration...
          </div>

          <!-- Loaded content -->
          <ng-container *ngIf="!accessMethodLoading">
            <p class="field-label">Choose how {{ uiService.productName }} connects to your AWS accounts:</p>

            <!-- CloudFormation Status Section (only shown when CloudFormation is selected) -->
            <ng-container *ngIf="selectedAccessMethod === AccessMethod.CLOUDFORMATION">
              <!-- CloudFormation Fully Configured Success Notice -->
              <div *ngIf="cloudFormationConfigured" class="setup-notice success">
                <div class="notice-icon">
                  <i class="fa-solid fa-circle-check"></i>
                </div>
                <div class="notice-content">
                  <p>CloudFormation Stacks are deployed and providing access to your AWS accounts.</p>
                </div>
              </div>

              <!-- CloudFormation Access Lost - before user selects a method -->
              <div *ngIf="cloudFormationAccessLost && !accessMethodDirty" class="setup-notice warning">
                <div class="notice-icon">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div class="notice-content">
                  <strong>Access not detected</strong>
                  <p>
                    Access appears to have been removed. Select an access method below to restore access to your AWS
                    accounts.
                  </p>
                </div>
              </div>

              <!-- CloudFormation Access Lost - after user confirms CloudFormation -->
              <div *ngIf="cloudFormationAccessLost && accessMethodDirty" class="setup-notice warning">
                <div class="notice-icon">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div class="notice-content">
                  <strong>Access not detected</strong>
                  <p>Follow steps to deploy CloudFormation templates to grant access.</p>
                </div>
                <button type="button" class="small-button arpio-button" (click)="navigateToUpgradeTab()">
                  CONFIGURE ACCESS
                </button>
              </div>

              <!-- CloudFormation Not Configured - currently using StackSets -->
              <div *ngIf="cloudFormationNotConfigured" class="stacksets-status-section">
                <div class="setup-notice warning">
                  <div class="notice-icon">
                    <i class="fa-utility fa-semibold fa-circle-exclamation"></i>
                  </div>
                  <div class="notice-content">
                    <p>
                      Arpio detected that you currently have access managed by StackSets. To switch to standalone
                      Stacks, you'll need to deploy CloudFormation Stack templates.
                    </p>
                  </div>
                  <button type="button" class="small-button arpio-button" (click)="navigateToUpgradeTab()">
                    SET UP ACCESS
                  </button>
                </div>

                <!-- Per-account status showing StackSet-managed status -->
                <div class="access-status-cards">
                  <!-- Primary Access Status -->
                  <div class="access-status-card">
                    <div class="status-header">
                      <i class="fa-solid fa-location-dot"></i>
                      <span>Primary Access</span>
                      <code>{{ sourceAccountId }} / {{ sourceRegion }}</code>
                    </div>
                    <div class="status-body">
                      <div *ngIf="sourceManagedByStackSet" class="status-item info">
                        <i class="fa-solid fa-circle-info"></i>
                        <span>Currently managed by StackSets - needs standalone CloudFormation Stack</span>
                      </div>
                      <div *ngIf="!sourceManagedByStackSet && sourceAccessService?.stack" class="status-item success">
                        <i class="fa-solid fa-circle-check"></i>
                        <span
                          >CloudFormation Stack <code>{{ sourceAccessService?.stack }}</code> deployed</span
                        >
                      </div>
                    </div>
                  </div>

                  <!-- Recovery Access Status -->
                  <div class="access-status-card">
                    <div class="status-header">
                      <i class="fa-solid fa-location-crosshairs"></i>
                      <span>Recovery Access</span>
                      <code>{{ targetAccountId }} / {{ targetRegion }}</code>
                    </div>
                    <div class="status-body">
                      <div *ngIf="targetManagedByStackSet" class="status-item info">
                        <i class="fa-solid fa-circle-info"></i>
                        <span>Currently managed by StackSets - needs standalone CloudFormation Stack</span>
                      </div>
                      <div *ngIf="!targetManagedByStackSet && targetAccessService?.stack" class="status-item success">
                        <i class="fa-solid fa-circle-check"></i>
                        <span
                          >CloudFormation Stack <code>{{ targetAccessService?.stack }}</code> deployed</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>

            <!-- StackSets Status Section (only shown when StackSets is selected) -->
            <ng-container *ngIf="selectedAccessMethod === AccessMethod.STACKSETS">
              <!-- StackSets Fully Configured Success Notice -->
              <div *ngIf="stackSetsConfigured" class="setup-notice success">
                <div class="notice-icon">
                  <i class="fa-solid fa-circle-check"></i>
                </div>
                <div class="notice-content">
                  <p>AWS StackSets are deployed and managing access to your AWS accounts.</p>
                </div>
              </div>

              <!-- StackSets Not Configured - show detailed status -->
              <div *ngIf="!stackSetsConfigured" class="stacksets-status-section">
                <!-- Overall warning -->
                <div class="setup-notice warning">
                  <div class="notice-icon">
                    <i class="fa-utility fa-semibold fa-circle-exclamation"></i>
                  </div>
                  <div class="notice-content">
                    <p *ngIf="stackSetsNotConfigured">
                      Before you can save changes here, this application needs AWS StackSets configured. This ensures
                      Arpio maintains access to your recovery environments.
                    </p>
                    <p *ngIf="stackSetsPartiallyConfigured">
                      AWS StackSets are partially configured. One or more accounts still need StackSets deployment.
                    </p>
                  </div>
                  <button type="button" class="small-button arpio-button" (click)="navigateToUpgradeTab()">
                    SET UP ACCESS
                  </button>
                </div>

                <!-- Per-account status -->
                <div class="access-status-cards">
                  <!-- Primary Access Status -->
                  <div class="access-status-card">
                    <div class="status-header">
                      <i class="fa-solid fa-location-dot"></i>
                      <span>Primary Access</span>
                      <code>{{ sourceAccountId }} / {{ sourceRegion }}</code>
                    </div>
                    <div class="status-body">
                      <div *ngIf="sourceManagedByStackSet" class="status-item success">
                        <i class="fa-solid fa-circle-check"></i>
                        <span>Managed by StackSets</span>
                      </div>
                      <div *ngIf="sourceHasCfnButNotStackSet" class="status-item info">
                        <i class="fa-solid fa-circle-info"></i>
                        <span
                          >CloudFormation Stack <code>{{ sourceAccessService?.stack }}</code> exists but is not managed
                          by StackSets</span
                        >
                      </div>
                      <div *ngIf="!sourceManagedByStackSet && !sourceHasCfnButNotStackSet" class="status-item warning">
                        <i class="fa-solid fa-circle-xmark"></i>
                        <span>StackSets not deployed</span>
                      </div>
                    </div>
                  </div>

                  <!-- Recovery Access Status -->
                  <div class="access-status-card">
                    <div class="status-header">
                      <i class="fa-solid fa-location-crosshairs"></i>
                      <span>Recovery Access</span>
                      <code>{{ targetAccountId }} / {{ targetRegion }}</code>
                    </div>
                    <div class="status-body">
                      <div *ngIf="targetManagedByStackSet" class="status-item success">
                        <i class="fa-solid fa-circle-check"></i>
                        <span>Managed by StackSets</span>
                      </div>
                      <div *ngIf="targetHasCfnButNotStackSet" class="status-item info">
                        <i class="fa-solid fa-circle-info"></i>
                        <span
                          >CloudFormation Stack <code>{{ targetAccessService?.stack }}</code> exists but is not managed
                          by StackSets</span
                        >
                      </div>
                      <div *ngIf="!targetManagedByStackSet && !targetHasCfnButNotStackSet" class="status-item warning">
                        <i class="fa-solid fa-circle-xmark"></i>
                        <span>StackSets not deployed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>

            <div class="access-method-cards">
              <!-- CloudFormation Templates Card -->
              <div
                class="access-method-card light-content-panel"
                [class.selected]="
                  selectedAccessMethod === AccessMethod.CLOUDFORMATION &&
                  (!cloudFormationAccessLost || accessMethodDirty)
                "
                [class.disabled]="!canModify"
                (click)="canModify && selectAccessMethod(AccessMethod.CLOUDFORMATION)"
              >
                <div class="card-header">
                  <input
                    type="radio"
                    name="selectedAccessMethod"
                    [value]="AccessMethod.CLOUDFORMATION"
                    [checked]="
                      selectedAccessMethod === AccessMethod.CLOUDFORMATION &&
                      (!cloudFormationAccessLost || accessMethodDirty)
                    "
                    [disabled]="!canModify"
                    (change)="selectAccessMethod(AccessMethod.CLOUDFORMATION)"
                  />
                  <h3>CloudFormation Templates</h3>
                </div>
                <div class="card-description">
                  Deploy access templates individually to each AWS account. Best for simple setups or accounts outside
                  an AWS Organization.
                </div>
                <div class="card-features">
                  <div class="feature"><i class="fa-solid fa-check"></i> Works with any AWS account</div>
                  <div class="feature"><i class="fa-solid fa-check"></i> No Organization required</div>
                </div>
              </div>

              <!-- AWS StackSets Card -->
              <div
                class="access-method-card light-content-panel"
                [class.selected]="selectedAccessMethod === AccessMethod.STACKSETS"
                [class.disabled]="!canModify"
                (click)="canModify && selectAccessMethod(AccessMethod.STACKSETS)"
              >
                <div class="card-header">
                  <input
                    type="radio"
                    name="selectedAccessMethod"
                    [value]="AccessMethod.STACKSETS"
                    [checked]="selectedAccessMethod === AccessMethod.STACKSETS"
                    [disabled]="!canModify"
                    (change)="selectAccessMethod(AccessMethod.STACKSETS)"
                  />
                  <h3>AWS StackSets</h3>
                </div>
                <div class="card-description">
                  Deploy once from your management account and automatically fan out to member accounts. Best for
                  multi-account Organizations.
                </div>
                <div class="card-features">
                  <div class="feature"><i class="fa-solid fa-check"></i> Centralized management</div>
                  <div class="feature"><i class="fa-solid fa-check"></i> Auto-deploy to new accounts</div>
                </div>
              </div>
            </div>
          </ng-container>
          <!-- End loaded content -->
        </div>
      </div>
      <ng-container *ngIf="canDelete">
        <hr />
        <div class="margin-top margin-bottom">
          <p><strong>Need to delete this application from your account?</strong></p>
          <a id="delete-button" data-cy="delete-button" class="underline" [routerLink]="" (click)="deleteClick()">
            Delete application...
          </a>
        </div>
      </ng-container>
    </div>
    <!-- END GENERAL TAB CONTENT -->

    <!-- RESOURCES TAB CONTENT -->
    <div *ngIf="activeTab === 'resources'" class="tab-content" id="resources-tab-content">
      <p class="double-margin-bottom" *ngIf="canModify">
        Configure which resources you want to be included in your Arpio application. Resources selected here will
        failover together.
      </p>
      <!-- App resource selection begins -->
      <div id="application-resource-selection"></div>
      <div class="control-header" id="application-resources-header">
        <div *ngIf="!canModify">View the resources and rules configured for this application.</div>
      </div>
      <app-resource-selection
        [formGroup]="applicationFormModel"
        [possibleTagValues]="possibleTagValues"
        [possibleResourceTypes]="possibleResourceTypes | async"
        [possibleResourceGroups]="possibleResourceGroups"
        [applicationType]="applicationType"
        [resources]="resources"
        [inclusionRulesCount$]="inclusionRulesCount$"
        [exclusionRulesCount$]="exclusionRulesCount$"
        [includeRulesOpen]="includeRulesOpen"
        [excludeRulesOpen]="excludeRulesOpen"
        [shouldObscureExclusionRules]="shouldObscureExclusionRules"
        [refreshing]="refreshing"
        [filterText]="filterText"
        [rpo]="rpo"
        [showRescanButton]="true"
        [readonly]="!canModify"
        (toggleIncludeRules)="toggleIncludeConditions()"
        (toggleExcludeRules)="toggleExcludeConditions()"
        (toggleExclusionRulesEnabled)="toggleExclusionRules()"
        (filterChange)="doFilterResources($event)"
        (refreshClick)="refreshButtonClick()"
      >
      </app-resource-selection>
    </div>
    <!-- END RESOURCES TAB CONTENT -->

    <!-- UPGRADE TAB CONTENT -->
    <div *ngIf="activeTab === 'upgrade'" class="tab-content" id="upgrade-tab-content">
      <div *ngIf="loadingAccessTemplates" class="notice-block">
        <i class="fa-solid fa-spinner fa-spin"></i> Loading access templates...
      </div>

      <ng-container *ngIf="!loadingAccessTemplates">
        <!-- Upgrade Notice with benefits list (hidden during migration to avoid confusion) -->
        <app-application-upgrade-notice
          *ngIf="!needsAccessConfiguration"
          [upgradeNeeded]="upgradeNeeded"
          [canModify]="canModify"
          [releaseDetails$]="releaseDetails$"
          [upgradeDetails]="upgradeDetails.bind(this)"
          [hasUpgradeDetails]="hasUpgradeDetails.bind(this)"
        ></app-application-upgrade-notice>

        <ng-container *ngIf="applicationType === 'aws'">
          <!-- CloudFormation method: show individual account access views -->
          <ng-container *ngIf="selectedAccessMethod === AccessMethod.CLOUDFORMATION">
            <!-- Access lost: prompt user to select a method before showing setup steps -->
            <ng-container
              *ngIf="cloudFormationAccessLost && showAccessTab && !accessMethodDirty; else normalSetupSteps"
            >
              <div class="setup-notice warning margin-top">
                <div class="notice-icon">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div class="notice-content">
                  <strong>Access not detected</strong>
                  <p>
                    Access appears to have been removed. Select your preferred access method under
                    <a class="inline-link" (click)="navigateToAccessMethod()">Access Method</a> in General settings.
                  </p>
                </div>
              </div>
            </ng-container>

            <!-- Normal setup steps: shown when access is healthy or user has selected a method -->
            <ng-template #normalSetupSteps>
              <div class="upgrade-section">
                <h2 *ngIf="upgradeNeeded">Update Access Templates</h2>
                <h2 *ngIf="!upgradeNeeded && cloudFormationNotConfigured">Set Up CloudFormation Access</h2>
                <p class="intro" *ngIf="upgradeNeeded">
                  We've enhanced {{ uiService.productName }}. To get this new functionality, update the CloudFormation
                  Stacks that grant {{ uiService.productName }} access to your AWS environment.
                </p>
                <p class="intro" *ngIf="!upgradeNeeded && cloudFormationNotConfigured">
                  Deploy CloudFormation templates to grant {{ uiService.productName }} access to your AWS accounts.
                  After deployment, you can optionally remove the StackSets from your management account.
                </p>
              </div>
              <aws-account-access-view
                class="margin-top"
                [state]="sourceAccessService?.state"
                [stackName]="sourceAccessService?.stack"
                [accessType]="AwsAccessScope.SOURCE"
                [templateUrl]="sourceAccessTemplate"
                [awsAccountId]="sourceAccountId"
                [region]="sourceRegion"
                [completedTestCount]="sourceAccessService?.tests"
                [managedByStackSet]="sourceManagedByStackSet"
                [stickyReferenceId]="'application-editor-tabs'"
                (buttonClicked)="sourceAccessService?.validateAccess()"
              >
              </aws-account-access-view>

              <aws-account-access-view
                class="margin-top"
                [state]="targetAccessService?.state"
                [stackName]="targetAccessService?.stack"
                [accessType]="AwsAccessScope.TARGET"
                [templateUrl]="targetAccessTemplate"
                [awsAccountId]="targetAccountId"
                [region]="targetRegion"
                [completedTestCount]="targetAccessService?.tests"
                [managedByStackSet]="targetManagedByStackSet"
                [stickyReferenceId]="'application-editor-tabs'"
                (buttonClicked)="targetAccessService?.validateAccess()"
              >
              </aws-account-access-view>
            </ng-template>
          </ng-container>

          <!-- StackSets method: show StackSets setup or update instructions -->
          <ng-container *ngIf="selectedAccessMethod === AccessMethod.STACKSETS">
            <!-- Setup/Migration mode: Use full stacksets-instructions with test access -->
            <ng-container *ngIf="needsStackSetsSetup">
              <div class="upgrade-section">
                <h2>Set Up StackSets Access</h2>
                <p class="intro">
                  You've chosen to manage {{ uiService.productName }} access with CloudFormation StackSets. Follow these
                  steps to deploy StackSets and grant {{ uiService.productName }} access to your AWS accounts.
                </p>
              </div>
              <stacksets-instructions
                class="margin-top"
                [migrationMode]="true"
                [hideHeader]="true"
                [sourceStackSetTemplateUrl]="sourceStackSetTemplateUrl"
                [targetStackSetTemplateUrl]="targetStackSetTemplateUrl"
                [sourceTemplateBucket]="sourceTemplateBucket"
                [sourceTemplateKey]="sourceTemplateKey"
                [targetTemplateBucket]="targetTemplateBucket"
                [targetTemplateKey]="targetTemplateKey"
                [sourceAccountId]="sourceAccountId"
                [sourceRegion]="sourceRegion"
                [targetAccountId]="targetAccountId"
                [targetRegion]="targetRegion"
                [sourceStackName]="sourceAccessService?.stack"
                [targetStackName]="targetAccessService?.stack"
                [sourceManagedByStackSet]="sourceManagedByStackSet"
                [targetManagedByStackSet]="targetManagedByStackSet"
                [stickyReferenceId]="'application-editor-tabs'"
                [testingSource]="isTestingSourceAccess"
                [testingTarget]="isTestingTargetAccess"
                [sourceError]="sourceAccessService?.hasApiError"
                [targetError]="targetAccessService?.hasApiError"
                [sourceTestCount]="sourceAccessService?.tests"
                [targetTestCount]="targetAccessService?.tests"
                (testAccess)="onTestAccess($event)"
                (finishSetup)="onFinishStackSetsSetup()"
              ></stacksets-instructions>
            </ng-container>

            <!-- Update mode: Show simpler update instructions -->
            <ng-container *ngIf="!needsStackSetsSetup">
              <stacksets-update-instructions
                class="margin-top"
                [sourceStackSetTemplateUrl]="sourceStackSetTemplateUrl"
                [targetStackSetTemplateUrl]="targetStackSetTemplateUrl"
                [sourceAccountId]="sourceAccountId"
                [sourceRegion]="sourceRegion"
                [targetAccountId]="targetAccountId"
                [targetRegion]="targetRegion"
                [sourceStackName]="sourceAccessService?.stack"
                [targetStackName]="targetAccessService?.stack"
                [sourceManagedByStackSet]="sourceManagedByStackSet"
                [targetManagedByStackSet]="targetManagedByStackSet"
                [sourceAccessState]="sourceAccessService?.state"
                [targetAccessState]="targetAccessService?.state"
                [sourceTestCount]="sourceAccessService?.tests"
                [targetTestCount]="targetAccessService?.tests"
                (testAccess)="onTestAccess($event)"
              ></stacksets-update-instructions>
            </ng-container>
          </ng-container>
        </ng-container>

        <ng-container *ngIf="applicationType === 'azure'">
          <div class="upgrade-section">
            <h2>Update Access Templates</h2>
            <p class="intro">
              We've enhanced {{ uiService.productName }}. To get this new functionality, update the Deployment Stacks
              that grant {{ uiService.productName }} access to your Azure environment.
            </p>
          </div>
          <azure-access-view
            class="margin-top"
            [state]="targetAccessService?.state"
            [accessType]="AwsAccessScope.TARGET"
            [installCommand]="targetInstallCommand"
            [subscriptionId]="targetAccountId"
            [region]="targetRegion"
            [completedTestCount]="targetAccessService?.tests"
            (buttonClicked)="targetAccessService?.validateAccess()"
          >
          </azure-access-view>
          <azure-access-view
            class="margin-top"
            [state]="sourceAccessService?.state"
            [accessType]="AwsAccessScope.SOURCE"
            [installCommand]="sourceInstallCommand"
            [subscriptionId]="sourceAccountId"
            [region]="sourceRegion"
            [completedTestCount]="sourceAccessService?.tests"
            (buttonClicked)="sourceAccessService?.validateAccess()"
          >
          </azure-access-view>
        </ng-container>
      </ng-container>
    </div>
    <!-- END UPGRADE TAB CONTENT -->
  </form>
</div>

<modal-dialog #deleteApplicationDialog [dialogTemplate]="deleteApplicationDialogTemplate"></modal-dialog>
<modal-dialog #unsavedChangesDialog [dialogTemplate]="unsavedChangesDialogTemplate"></modal-dialog>

<ng-template #deleteApplicationDialogTemplate>
  <div class="flex-center-column full-width">
    <confirm-action-dialog
      name="&quot;{{ application.name }}&quot;"
      [error]="null"
      (onCancel)="dismissDeleteModal()"
      (onAction)="deleteApplication()"
      [busy]="deletingApplication"
    >
      <div class="error margin-top">
        Note: Deleting this application will destroy resources in the recovery environment that are not included in
        another application. After deletion, you will no longer be able to recover the application in the event of a
        disaster.
      </div>
    </confirm-action-dialog>
  </div>
</ng-template>

<ng-template #unsavedChangesDialogTemplate>
  <div class="unsaved-changes-dialog light-content-panel flex-left-column light-control-host">
    <h3 class="panel-title">Unsaved Changes</h3>
    <p class="margin-top">You have unsaved changes that will be lost if you leave this page:</p>
    <div class="changes-summary margin-top">
      <div *ngFor="let group of pendingChanges" class="change-group">
        <div class="change-category">{{ group.category }}</div>
        <ul class="change-list">
          <li *ngFor="let change of group.changes">{{ change }}</li>
        </ul>
      </div>
    </div>
    <div class="flex-center-row margin-top full-width">
      <div class="spacer"></div>
      <button class="arpio-secondary-button" type="button" (click)="dismissUnsavedChangesDialog()">KEEP EDITING</button>
      <button class="arpio-button margin-left" type="button" (click)="confirmDiscardChanges()">DISCARD CHANGES</button>
      <div class="spacer"></div>
    </div>
  </div>
</ng-template>
